import { Button, Flex, Form, Modal, type ModalProps, Row } from 'antd';
import { message } from 'antd';
import { FC, useEffect, useState } from 'react';

import { useAdminBobContactController_create } from '@api-client/generated/AdminBobContactController/create';
import { useAdminBobContactController_suggestBobId } from '@api-client/generated/AdminBobContactController/suggestBobId';
import { Schemas } from '@api-client/generated/types';
import { DEFAULT_COUNTRY_CODE } from '@constants';
import { ContactsWrapperFormControl } from '@entities';
import { useCurrentCompanyId } from '@hooks';
import { countries } from '@utils/countries';

import { bobIdError, normaliseBobId } from './bobId';
import * as S from './styled';

type ContactActionProps = {
  onCancel: () => void;
  onAfterCreate?: (contact: Schemas.BobContact) => void;
} & ModalProps;

/**
 * Which BOBLink module the contact is imported under.
 *
 * `both` is a first-class choice, not a workaround: BOB50 holds one record carrying both flags,
 * and an accountant who deals with a counterparty on both sides has to be able to say so. The
 * default is supplier because the only place this modal opens from is the expense flow.
 */
const ROLE_OPTIONS: {
  label: string;
  value: Schemas.AdminBobContactDto['role'];
}[] = [
  { label: 'Supplier', value: 'supplier' },
  { label: 'Customer', value: 'customer' },
  { label: 'Customer and supplier', value: 'both' },
];

const DEFAULT_ROLE: Schemas.AdminBobContactDto['role'] = 'supplier';

const BOB_ID_TAKEN = 'BOB_ID_TAKEN';

/**
 * The reference's syntax rules, hoisted so the array keeps one identity across renders.
 *
 * Rejects with a string, which is what rc-field-form collects rule errors as. The rule blocks the
 * submit as well as rendering the message, so nothing invalid reaches the request — asserted in
 * `e2e/contact-create.spec.ts` by watching for the POST, not just for the message.
 */
const BOB_ID_RULES = [
  {
    validator: (_rule: unknown, value: string) => {
      const error = bobIdError(value);
      return error ? Promise.reject(error) : Promise.resolve();
    },
  },
];

const ContactAction: FC<ContactActionProps> = ({
  open,
  onAfterCreate,
  onCancel,
  ...rest
}) => {
  const companyId = useCurrentCompanyId();

  const [form] = Form.useForm();

  // The name a reference has been asked for. Empty until the accountant leaves the name field,
  // because suggesting from a half-typed name would fill the field with something they never saw
  // a whole word of.
  const [suggestFor, setSuggestFor] = useState('');
  // Suggestion mode. Editing the reference by hand ends it — the accountant has taken the field
  // over, and overwriting what they typed on the next name change would be the rudest possible
  // behaviour. The regenerate control hands it back.
  const [suggesting, setSuggesting] = useState(true);

  const { data: suggested, isFetching: suggestionLoading } =
    useAdminBobContactController_suggestBobId({
      params: { companyId, name: suggestFor },
      config: {
        enabled: Boolean(suggestFor) && suggesting,
        // One answer per name, kept: re-opening the same name must not spend a Lambda call, and
        // the packer is deterministic anyway.
        staleTime: Infinity,
        // A failed suggestion is not worth retrying in front of someone waiting to type: the
        // field stays blank and they fill it in.
        retry: false,
      },
    });

  useEffect(() => {
    // A null reference is an answer, not a failure — the packer may be unreachable or every
    // candidate taken. Leave the field alone and let the accountant type one.
    if (suggesting && suggested?.bobId) {
      form.setFieldValue('bobId', suggested.bobId);
    }
  }, [form, suggested, suggesting]);

  const { mutate: createContact, isPending: loading } =
    useAdminBobContactController_create();

  const resetState = () => {
    form.resetFields();
    setSuggestFor('');
    setSuggesting(true);
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  const handleNameBlur = () => {
    const name = (form.getFieldValue('name') ?? '').trim();
    if (name) {
      setSuggestFor(name);
    }
  };

  const handleRegenerate = () => {
    setSuggesting(true);
    const name = (form.getFieldValue('name') ?? '').trim();
    setSuggestFor(name);
    if (suggestFor === name && suggested?.bobId) {
      // Same name, cached answer: nothing will refetch, so put it back by hand.
      form.setFieldValue('bobId', suggested.bobId);
    }
  };

  const handleSubmit = (values: Schemas.AdminBobContactDto) => {
    const bobId = normaliseBobId(values.bobId);

    createContact(
      {
        parameter: {
          companyId,
        },
        // The backend validates the reference and stores it verbatim, so lower case sent here
        // would stay lower case in the dossier forever.
        requestBody: { ...values, bobId },
      },
      {
        onSuccess: (response) => {
          // Naming the reference is the point: it is what the accountant will look for in BOB50,
          // and it does not appear there until the automator's next import cycle.
          message.success(
            `Created — BOB reference: ${response.bobId}. It will appear in BOB50 after the next sync cycle.`
          );

          onAfterCreate?.(response);
          handleCancel();
        },
        onError: (error) => {
          // The one rule that cannot be checked in the browser: whether this reference is already
          // used in this company. Rendered against the field rather than as a toast, because the
          // fix is to change that field.
          const conflict = error.response?.data as unknown as
            | Schemas.AdminBobContactConflictDto
            | undefined;
          if (
            error.response?.status === 409 &&
            conflict?.code === BOB_ID_TAKEN
          ) {
            setSuggesting(false);
            form.setFields([
              {
                name: 'bobId',
                errors: [
                  'That reference is already used by another contact in this company',
                ],
              },
            ]);
            return;
          }
          message.error('Could not create the contact');
        },
      }
    );
  };

  return (
    <Modal
      width={575}
      open={open}
      title="Add new contact"
      footer={null}
      onCancel={handleCancel}
      destroyOnClose
      centered
      {...rest}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          role: DEFAULT_ROLE,
          address: {
            countryCode: DEFAULT_COUNTRY_CODE,
          },
        }}
      >
        <S.Card>
          <Row gutter={[12, 0]}>
            <ContactsWrapperFormControl
              type="input"
              span={14}
              form={{
                label: 'Contact name',
                name: 'name',
                rules: [{ required: true }],
              }}
              control={{ onBlur: handleNameBlur }}
            />

            <ContactsWrapperFormControl
              type="select"
              span={10}
              form={{
                label: 'Relationship',
                name: 'role',
                rules: [{ required: true }],
              }}
              control={{ options: ROLE_OPTIONS }}
            />
          </Row>

          <Row gutter={[12, 0]}>
            <ContactsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'VAT ID (optional)',
                name: 'vatId',
              }}
            />

            <ContactsWrapperFormControl
              type="input"
              span={12}
              control={{ onChange: () => setSuggesting(false) }}
              form={{
                label: 'BOB50 ID',
                name: 'bobId',
                // Upper-cased as it is typed rather than on submit, so what is on screen is what
                // will be stored.
                normalize: (value: string) => (value ?? '').toUpperCase(),
                rules: BOB_ID_RULES,
                extra: (
                  <S.Regenerate
                    type="link"
                    size="small"
                    onClick={handleRegenerate}
                    disabled={suggestionLoading}
                  >
                    {suggestionLoading
                      ? 'Suggesting…'
                      : 'Suggest from the name'}
                  </S.Regenerate>
                ),
              }}
            />
          </Row>

          <Row gutter={[12, 0]}>
            <ContactsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'IBAN (optional)',
                name: 'iban',
              }}
            />

            <ContactsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'BIC (optional)',
                name: 'bic',
              }}
            />
          </Row>
        </S.Card>

        <S.Card>
          <Row gutter={[12, 0]}>
            <ContactsWrapperFormControl
              type="input"
              span={10}
              form={{
                label: 'Street',
                name: ['address', 'addressLine'],
              }}
            />

            <ContactsWrapperFormControl
              type="input"
              span={7}
              form={{
                label: 'Number',
                name: ['address', 'houseNumber'],
              }}
            />

            <ContactsWrapperFormControl
              type="input"
              span={7}
              form={{
                label: 'Postal code',
                name: ['address', 'postalCode'],
              }}
            />

            <ContactsWrapperFormControl
              type="select"
              span={10}
              form={{
                label: 'Country',
                name: ['address', 'countryCode'],
              }}
              control={{
                options: countries.map((country) => ({
                  label: country.name,
                  value: country.value,
                })),
                virtual: true,
                optionFilterProp: 'label',
              }}
            />

            <ContactsWrapperFormControl
              type="input"
              span={14}
              form={{
                label: 'City',
                name: ['address', 'city'],
              }}
            />
          </Row>
        </S.Card>

        <S.Card>
          <Row gutter={[12, 0]}>
            <ContactsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'Email (optional)',
                name: 'email',
              }}
            />

            <ContactsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'Phone number (optional)',
                name: 'phone',
              }}
            />
          </Row>
        </S.Card>

        <S.Submit>
          <Form.Item noStyle>
            <Flex justify="flex-end" gap={12}>
              <Button onClick={handleCancel} size="large">
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
              >
                Add
              </Button>
            </Flex>
          </Form.Item>
        </S.Submit>
      </Form>
    </Modal>
  );
};

export default ContactAction;
